import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
    PackageX, CalendarClock, MapPin, RotateCcw, AlertTriangle, Store as StoreIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
// A factory, not a hook, despite the filename — imported under its real name so
// eslint's react-hooks/rules-of-hooks does not read the call below as a
// misplaced hook. hooks/usePermissions.js explains the trap in full.
import createAuthedAxios from '../../hooks/useAxiosAuth';

// The two questions a pharmacy has to answer before it opens: what is about to
// run out, and what is about to become unsellable.
//
// ONE SCREEN, TWO TABS, BECAUSE IT IS ONE WALK. Both lists end with somebody
// leaving the counter and going to a shelf, and both are read at the same time
// of day by the same person. Splitting them into separate pages would mean the
// person who checks reorders in the morning never sees that a third of the
// stock they are about to reorder expires next week.
//
// EVERY ROW CARRIES ITS BIN. A reorder list without a location is a list of
// names — the point of it is that somebody can walk to A-12 and count what is
// there. When the bin is empty that is itself the finding: nothing else in the
// system will ever say that a medicine has no home shelf, and stock with no
// address is stock that gets re-bought because nobody could find it.

const NUMBER = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });
const INR = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const qty = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? NUMBER.format(n) : '0';
};
const rupees = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? `₹${INR.format(n)}` : '-';
};

// DATEONLY arrives as 'YYYY-MM-DD' and is deliberately never passed through
// `new Date()`: that parses as UTC midnight, so an expiry of 2026-08-01 renders
// as 31 July for anybody west of Greenwich. On this screen in particular that
// is not a cosmetic slip — it is a carton reading as a day more expired than it
// is, on the one report whose whole job is to be trusted about expiry.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dateOnly = (value) => {
    if (!value) return null;
    const [y, m, d] = String(value).slice(0, 10).split('-');
    if (!y || !m || !d) return String(value);
    return `${d} ${MONTHS[Number(m) - 1] || m} ${y}`;
};

// The windows a buyer actually thinks in. 90 is the default because that is
// roughly the notice a supplier wants for a return-to-supplier claim — shorter
// than that and the answer is usually "too late, sell it or write it off".
const DAY_PRESETS = [30, 60, 90, 180];
const DEFAULT_DAYS = 90;
const MAX_DAYS = 730;

const SCHEDULE_STYLES = {
    H: 'bg-orange-50 text-orange-700 border-orange-200',
    H1: 'bg-red-50 text-red-700 border-red-200',
    X: 'bg-purple-50 text-purple-700 border-purple-200',
};

const errorText = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

/** The bin, drawn the same way on both tabs so the eye finds it in one place. */
const BinChip = ({ binCode, rackName, t }) => {
    if (!binCode) {
        // Not "-". A medicine with no home bin cannot be picked without asking
        // somebody, and this report is the only screen that would ever say so.
        return (
            <span
                data-testid="bin-missing"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-semibold"
                title={t('stockalerts.no_bin_hint', 'No shelf location set for this medicine')}
            >
                <AlertTriangle className="w-3 h-3" />
                {t('stockalerts.no_bin', 'no bin set')}
            </span>
        );
    }
    return (
        <span className="inline-flex flex-col">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#4ABDBD]/10 text-[#2D8A8A] border border-[#4ABDBD]/30 text-[11px] font-mono font-bold">
                <MapPin className="w-3 h-3" />
                {binCode}
            </span>
            {rackName && <span className="text-[10px] text-slate-400 mt-0.5 pl-1">{rackName}</span>}
        </span>
    );
};

export default function StockAlerts() {
    const { t } = useTranslation();

    // One memoised instance: it reads the access token in its request
    // interceptor, so the same copy stays correct across a token refresh.
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    const [activeTab, setActiveTab] = useState('low');
    const [stores, setStores] = useState([]);
    const [storeId, setStoreId] = useState('');

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [valueAtRisk, setValueAtRisk] = useState(0);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [days, setDays] = useState(DEFAULT_DAYS);
    const [daysInput, setDaysInput] = useState(String(DEFAULT_DAYS));
    const [refreshKey, setRefreshKey] = useState(0);

    const daysTimeoutRef = useRef(null);

    // Typing "180" passes through 1 and 18 on the way. Debounced so the report
    // is fetched for the number the user meant rather than three times for
    // numbers they were only halfway through.
    useEffect(() => {
        if (daysTimeoutRef.current) clearTimeout(daysTimeoutRef.current);
        daysTimeoutRef.current = setTimeout(() => {
            const parsed = parseInt(daysInput, 10);
            const next = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), MAX_DAYS) : DEFAULT_DAYS;
            setDays(next);
            setCurrentPage(1);
        }, 500);
        return () => { if (daysTimeoutRef.current) clearTimeout(daysTimeoutRef.current); };
    }, [daysInput]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get('/pharmacy/stores?status=active');
                if (!cancelled) setStores(res?.data?.data || []);
            } catch {
                // Without the picker the report covers every counter, which is
                // the default anyway. Not worth its own toast.
            }
        })();
        return () => { cancelled = true; };
    }, [axiosInstance]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const qs = new URLSearchParams();
                qs.append('page', String(currentPage));
                qs.append('limit', String(itemsPerPage));
                if (storeId) qs.append('storeId', storeId);
                if (activeTab === 'expiring') qs.append('days', String(days));

                const path = activeTab === 'low' ? '/pharmacy/stock/low' : '/pharmacy/stock/expiring';
                const res = await axiosInstance.get(`${path}?${qs.toString()}`);
                if (cancelled) return;
                setRows(res?.data?.data || []);
                setTotal(res?.data?.total || 0);
                // Totalled across every matching batch by the server, not summed
                // from this page — a headline figure that shrinks as the user
                // pages forward is worse than showing none at all.
                setValueAtRisk(res?.data?.valueAtRisk || 0);
            } catch (error) {
                if (cancelled) return;
                setRows([]);
                setTotal(0);
                setValueAtRisk(0);
                toast.warn(errorText(error, activeTab === 'low'
                    ? t('stockalerts.low_error', 'Failed to load the reorder list')
                    : t('stockalerts.expiry_error', 'Failed to load the expiry report')));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [axiosInstance, activeTab, storeId, days, currentPage, itemsPerPage, refreshKey]);

    const switchTab = (tab) => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        // The two lists have nothing in common but their length, so the old page
        // number is meaningless against the new one — page 4 of the reorder list
        // is very often past the end of the expiry list.
        setCurrentPage(1);
        setRows([]);
    };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;
    const isLow = activeTab === 'low';

    return (
        <div data-testid="stock-alerts-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="stock-alerts-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('stockalerts.title', 'Stock alerts')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('stockalerts.subtitle', 'What is running out and what is running out of time. Every row carries its bin, so the next step is a walk to the shelf.')}
                    </p>
                </div>
                <button
                    data-testid="stock-alerts-refresh"
                    onClick={() => setRefreshKey((k) => k + 1)}
                    disabled={loading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 whitespace-nowrap ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    <RotateCcw className="w-4 h-4" />
                    {t('stockalerts.refresh', 'Refresh')}
                </button>
            </div>

            {/* ---------------------------------------------------- tabs ---- */}
            <div className="mb-4 flex flex-col lg:flex-row gap-2 lg:items-center">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    {[
                        { id: 'low', label: t('stockalerts.tab_low', 'Low stock'), Icon: PackageX },
                        { id: 'expiring', label: t('stockalerts.tab_expiring', 'Expiring'), Icon: CalendarClock },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            data-testid={`stock-tab-${tab.id}`}
                            onClick={() => switchTab(tab.id)}
                            className={`relative px-5 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 whitespace-nowrap ${activeTab === tab.id ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === tab.id && (
                                <motion.div
                                    layoutId="activeStockAlertTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20 inline-flex items-center gap-2">
                                <tab.Icon className="w-4 h-4" />
                                {tab.label}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full lg:w-60">
                    <select
                        data-testid="stock-store-filter"
                        value={storeId}
                        onChange={(e) => { setStoreId(e.target.value); setCurrentPage(1); }}
                        className="tt-select w-full p-2 pl-9 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    >
                        <option value="">{t('stockalerts.all_stores', 'All counters')}</option>
                        {stores.map((s) => (
                            <option key={s.storeId} value={s.storeId}>{s.name}</option>
                        ))}
                    </select>
                    <StoreIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                {!isLow && (
                    <div className="flex items-center gap-2 flex-wrap" data-testid="expiry-window-picker">
                        <span className="text-xs text-slate-500 whitespace-nowrap">{t('stockalerts.window', 'Expiring within')}</span>
                        {DAY_PRESETS.map((d) => (
                            <button
                                key={d}
                                data-testid={`expiry-preset-${d}`}
                                // Both, and deliberately. Routing a preset
                                // through the debounce alone would leave the
                                // button un-highlighted for half a second after
                                // a click, which reads as the click not landing.
                                // The debounce then re-sets the same value and
                                // React bails out, so nothing fetches twice.
                                onClick={() => { setDaysInput(String(d)); setDays(d); setCurrentPage(1); }}
                                className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${days === d
                                    ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white border-transparent shadow'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-[#4ABDBD]'}`}
                            >
                                {d}d
                            </button>
                        ))}
                        <input
                            data-testid="expiry-days-input"
                            type="number"
                            min={0}
                            max={MAX_DAYS}
                            value={daysInput}
                            onChange={(e) => setDaysInput(e.target.value.slice(0, 3))}
                            className="w-20 p-1.5 border border-gray-300 rounded-xl text-xs text-center focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            aria-label={t('stockalerts.days_label', 'Days')}
                        />
                    </div>
                )}
            </div>

            {/* ------------------------------------------------- headline ---- */}
            <div className="mb-4 flex flex-wrap gap-3">
                <div className="px-3 py-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 border border-[#4ABDBD]/20">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                        {isLow ? t('stockalerts.count_low', 'Items at or below reorder level') : t('stockalerts.count_expiring', 'Batches in this window')}
                    </div>
                    <div className="text-lg font-bold text-[#2D8A8A]" data-testid="stock-alert-count">{total}</div>
                </div>
                {!isLow && (
                    <div className="px-3 py-2 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
                        <div className="text-[10px] uppercase tracking-wider text-amber-600/70">
                            {t('stockalerts.value_at_risk', 'Value at risk (at MRP)')}
                        </div>
                        <div className="text-lg font-bold text-amber-700" data-testid="stock-value-at-risk">{rupees(valueAtRisk)}</div>
                    </div>
                )}
            </div>

            {!isLow && (
                <p className="mb-3 text-[11px] text-slate-400 leading-relaxed">
                    {/* Both of these are in the list on purpose and neither is
                        obvious, so the report says so rather than leaving
                        somebody to wonder why a negative number is showing. */}
                    {t('stockalerts.expiry_note', 'Already-expired batches are listed first — they are unsellable, still counted in the valuation, and still sitting where someone could hand them over. Batches with no expiry keyed appear last: the counter refuses to sell them, so nothing else would ever surface them.')}
                </p>
            )}

            {/* ---------------------------------------------------- table ---- */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('stockalerts.loading', 'Loading...')}</span>
                </div>
            ) : isLow ? (
                <LowStockTable rows={rows} indexOfFirst={indexOfFirst} t={t} />
            ) : (
                <ExpiringTable rows={rows} indexOfFirst={indexOfFirst} t={t} />
            )}

            {!loading && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}
        </div>
    );
}

/* ==================================================================== *
 * TAB 1 — at or below reorder level.
 * ==================================================================== */

function LowStockTable({ rows, indexOfFirst, t }) {
    return (
        <>
            <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                <table data-testid="low-stock-table" className="w-full border-collapse rounded-md overflow-hidden">
                    <thead>
                        <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('stockalerts.serial_no', 'S.No')}</th>
                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('stockalerts.medicine', 'Medicine')}</th>
                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('stockalerts.bin', 'Bin')}</th>
                            <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('stockalerts.on_hand', 'On hand')}</th>
                            <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('stockalerts.reorder_level', 'Reorder at')}</th>
                            <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('stockalerts.short_by', 'Short by')}</th>
                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('stockalerts.state', 'State')}</th>
                        </tr>
                    </thead>
                    <tbody data-testid="low-stock-table-body">
                        {rows.length > 0 ? rows.map((r, index) => (
                            <tr
                                key={r.medicineId}
                                data-testid={`low-stock-row-${r.medicineId}`}
                                className={`border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 ${r.outOfStock ? 'bg-red-50/40' : ''}`}
                            >
                                <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                <td className="p-3 text-xs">
                                    <div className="font-semibold text-gray-700">{r.name}</div>
                                    <div className="text-[11px] text-slate-400">
                                        {[r.genericName, r.strength, r.form].filter(Boolean).join(' · ') || r.brand || ''}
                                    </div>
                                    {r.schedule && r.schedule !== 'none' && (
                                        <span className={`inline-flex mt-1 px-1.5 py-0.5 rounded border text-[10px] font-bold ${SCHEDULE_STYLES[r.schedule] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                            {t('stockalerts.schedule', 'Schedule')} {r.schedule}
                                        </span>
                                    )}
                                </td>
                                <td className="p-3 text-xs"><BinChip binCode={r.binCode} rackName={r.rackName} t={t} /></td>
                                <td className="p-3 text-xs text-right">
                                    <span className={`font-bold ${r.outOfStock ? 'text-red-600' : 'text-gray-800'}`}>{qty(r.qtyOnHand)}</span>
                                    <span className="text-slate-400"> {r.unit || ''}</span>
                                </td>
                                <td className="p-3 text-xs text-right text-gray-600">{qty(r.reorderLevel)}</td>
                                <td className="p-3 text-xs text-right font-semibold text-amber-700">{qty(r.shortBy)}</td>
                                <td className="p-3 text-xs">
                                    {r.outOfStock ? (
                                        <span className="inline-flex px-2 py-0.5 rounded-full border border-red-200 bg-red-50 text-red-700 text-[11px] font-semibold">
                                            {t('stockalerts.out_of_stock', 'Out of stock')}
                                        </span>
                                    ) : (
                                        <span className="inline-flex px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[11px] font-semibold">
                                            {t('stockalerts.low', 'Low')}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={7} className="p-6 text-center text-gray-500">
                                    {t('stockalerts.no_low', 'Nothing is below its reorder level. Only medicines with a reorder level set can appear here.')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="block lg:hidden space-y-3">
                {rows.length > 0 ? rows.map((r) => (
                    <div key={r.medicineId} data-testid={`low-stock-card-${r.medicineId}`} className="p-4 bg-gray-50 rounded-xl shadow-md">
                        <div className="flex justify-between items-start gap-2">
                            <div>
                                <div className="font-semibold text-gray-800 text-sm">{r.name}</div>
                                <div className="text-[11px] text-slate-400">
                                    {[r.genericName, r.strength, r.form].filter(Boolean).join(' · ') || r.brand || ''}
                                </div>
                            </div>
                            {r.outOfStock ? (
                                <span className="inline-flex px-2 py-0.5 rounded-full border border-red-200 bg-red-50 text-red-700 text-[11px] font-semibold whitespace-nowrap">
                                    {t('stockalerts.out_of_stock', 'Out of stock')}
                                </span>
                            ) : (
                                <span className="inline-flex px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[11px] font-semibold">
                                    {t('stockalerts.low', 'Low')}
                                </span>
                            )}
                        </div>
                        <div className="mt-2"><BinChip binCode={r.binCode} rackName={r.rackName} t={t} /></div>
                        <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                            <div>
                                <div className="text-slate-400">{t('stockalerts.on_hand', 'On hand')}</div>
                                <div className={`font-bold ${r.outOfStock ? 'text-red-600' : 'text-gray-800'}`}>{qty(r.qtyOnHand)} {r.unit || ''}</div>
                            </div>
                            <div>
                                <div className="text-slate-400">{t('stockalerts.reorder_level', 'Reorder at')}</div>
                                <div className="text-gray-700">{qty(r.reorderLevel)}</div>
                            </div>
                            <div>
                                <div className="text-slate-400">{t('stockalerts.short_by', 'Short by')}</div>
                                <div className="font-semibold text-amber-700">{qty(r.shortBy)}</div>
                            </div>
                        </div>
                    </div>
                )) : (
                    <div className="p-6 text-center text-gray-500 text-sm">
                        {t('stockalerts.no_low', 'Nothing is below its reorder level. Only medicines with a reorder level set can appear here.')}
                    </div>
                )}
            </div>
        </>
    );
}

/* ==================================================================== *
 * TAB 2 — expiring, expired, and undated.
 * ==================================================================== */

/** How urgent this batch is, said in the words somebody would use out loud. */
const expiryLabel = (r, t) => {
    if (r.undated) return { text: t('stockalerts.undated', 'No expiry keyed'), style: 'bg-gray-100 text-gray-600 border-gray-200' };
    if (r.expired) return { text: t('stockalerts.expired', 'Expired'), style: 'bg-red-50 text-red-700 border-red-200' };
    if (r.daysLeft !== null && r.daysLeft <= 30) {
        return { text: `${r.daysLeft} ${t('stockalerts.days_left', 'days left')}`, style: 'bg-orange-50 text-orange-700 border-orange-200' };
    }
    return { text: `${r.daysLeft} ${t('stockalerts.days_left', 'days left')}`, style: 'bg-amber-50 text-amber-700 border-amber-200' };
};

function ExpiringTable({ rows, indexOfFirst, t }) {
    return (
        <>
            <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                <table data-testid="expiring-stock-table" className="w-full border-collapse rounded-md overflow-hidden">
                    <thead>
                        <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('stockalerts.serial_no', 'S.No')}</th>
                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('stockalerts.medicine', 'Medicine')}</th>
                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('stockalerts.batch', 'Batch')}</th>
                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('stockalerts.bin', 'Bin')}</th>
                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('stockalerts.expiry', 'Expiry')}</th>
                            <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('stockalerts.on_hand', 'On hand')}</th>
                            <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('stockalerts.mrp', 'MRP')}</th>
                            <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('stockalerts.at_risk', 'At risk')}</th>
                        </tr>
                    </thead>
                    <tbody data-testid="expiring-stock-table-body">
                        {rows.length > 0 ? rows.map((r, index) => {
                            const badge = expiryLabel(r, t);
                            return (
                                <tr
                                    key={r.batchId}
                                    data-testid={`expiring-row-${r.batchId}`}
                                    className={`border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 ${r.expired ? 'bg-red-50/40' : ''}`}
                                >
                                    <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                    <td className="p-3 text-xs">
                                        <div className="font-semibold text-gray-700">{r.name}</div>
                                        <div className="text-[11px] text-slate-400">{[r.genericName, r.brand].filter(Boolean).join(' · ')}</div>
                                    </td>
                                    <td className="p-3 text-xs">
                                        <div className="font-mono text-gray-700">{r.batchNo || '-'}</div>
                                        {r.status && r.status !== 'active' && (
                                            <span className="inline-flex mt-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-semibold capitalize">
                                                {r.status}
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-3 text-xs"><BinChip binCode={r.binCode} rackName={r.rackName} t={t} /></td>
                                    <td className="p-3 text-xs">
                                        <div className="text-gray-700">{dateOnly(r.expiryDate) || '-'}</div>
                                        <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${badge.style}`}>
                                            {badge.text}
                                        </span>
                                    </td>
                                    <td className="p-3 text-xs text-right font-bold text-gray-800">
                                        {qty(r.qtyOnHand)}<span className="font-normal text-slate-400"> {r.unit || ''}</span>
                                    </td>
                                    <td className="p-3 text-xs text-right text-gray-600">{rupees(r.mrp)}</td>
                                    <td className="p-3 text-xs text-right font-semibold text-amber-700">{rupees(r.valueAtRisk)}</td>
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan={8} className="p-6 text-center text-gray-500">
                                    {t('stockalerts.no_expiring', 'Nothing expiring in this window. Widen it if you are planning a supplier return.')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="block lg:hidden space-y-3">
                {rows.length > 0 ? rows.map((r) => {
                    const badge = expiryLabel(r, t);
                    return (
                        <div key={r.batchId} data-testid={`expiring-card-${r.batchId}`} className="p-4 bg-gray-50 rounded-xl shadow-md">
                            <div className="flex justify-between items-start gap-2">
                                <div>
                                    <div className="font-semibold text-gray-800 text-sm">{r.name}</div>
                                    <div className="text-[11px] text-slate-400 font-mono">{r.batchNo || '-'}</div>
                                </div>
                                <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${badge.style}`}>
                                    {badge.text}
                                </span>
                            </div>
                            <div className="mt-2"><BinChip binCode={r.binCode} rackName={r.rackName} t={t} /></div>
                            <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                                <div>
                                    <div className="text-slate-400">{t('stockalerts.expiry', 'Expiry')}</div>
                                    <div className="text-gray-700">{dateOnly(r.expiryDate) || '-'}</div>
                                </div>
                                <div>
                                    <div className="text-slate-400">{t('stockalerts.on_hand', 'On hand')}</div>
                                    <div className="font-bold text-gray-800">{qty(r.qtyOnHand)} {r.unit || ''}</div>
                                </div>
                                <div>
                                    <div className="text-slate-400">{t('stockalerts.at_risk', 'At risk')}</div>
                                    <div className="font-semibold text-amber-700">{rupees(r.valueAtRisk)}</div>
                                </div>
                            </div>
                        </div>
                    );
                }) : (
                    <div className="p-6 text-center text-gray-500 text-sm">
                        {t('stockalerts.no_expiring', 'Nothing expiring in this window. Widen it if you are planning a supplier return.')}
                    </div>
                )}
            </div>
        </>
    );
}
